// (c) chaehongee. Confidential and proprietary.

import { useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { cancelCreditPurchase } from '../../services/billing'
import { clearCheckoutReturnTo, getCheckoutReturnTo } from '../../services/checkoutReturn'
import { getUserProfile } from '../../services/user'

import styles from './StripeCheckoutCallback.module.css'

const FALLBACK_RETURN_TO = '/dashboard?tab=billing'

export function StripeCheckoutCallback(): ReactElement {
    const { t } = useTranslation('billing')
    const [message, setMessage] = useState(() => t('finalizingPurchase'))

    useEffect(() => {
        const run = async () => {
            const params = new URLSearchParams(window.location.search)
            const checkout = params.get('checkout')
            const purchaseId = params.get('purchase_id')

            if (checkout === 'cancel' && purchaseId) {
                setMessage(t('purchaseCanceledReturning'))
                try {
                    await cancelCreditPurchase(purchaseId)
                } catch {
                    // Best-effort; still return the user to the app.
                }
            } else if (checkout === 'success') {
                setMessage(t('paymentReceived'))
                await bestEffortRefreshCredits()
            } else {
                setMessage(t('returning'))
            }

            let returnTo = getCheckoutReturnTo() ?? FALLBACK_RETURN_TO
            if (checkout === 'cancel' && purchaseId && isBillingReturnTo(returnTo)) {
                // Preserve existing BillingInvoices UX (shows "Purchase canceled." and
                // reconciles server-side status) by passing the cancel markers through.
                returnTo = addQueryParams(returnTo, {
                    checkout: 'cancel',
                    purchase_id: purchaseId,
                })
            }
            clearCheckoutReturnTo()
            window.location.replace(returnTo)
        }

        void run()
    }, [t])

    return (
        <div className={styles.root}>
            <div className={styles.card}>
                <h2 className={`branded-title ${styles.title}`}>{t('stripeCheckoutTitle')}</h2>
                <p className={styles.body}>{message}</p>
            </div>
        </div>
    )
}

async function bestEffortRefreshCredits(): Promise<void> {
    // Credits are applied asynchronously via webhook; poll briefly to reduce
    // the chance the user returns to chat before credits show up.
    for (const delayMs of [0, 500, 1000, 1500, 2000]) {
        if (delayMs > 0) {
            await sleep(delayMs)
        }
        try {
            await getUserProfile()
            return
        } catch {
            // keep trying
        }
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function isBillingReturnTo(path: string): boolean {
    return path.startsWith('/dashboard')
}

function addQueryParams(path: string, nextParams: Record<string, string>): string {
    const url = new URL(path, window.location.origin)
    for (const [key, value] of Object.entries(nextParams)) {
        url.searchParams.set(key, value)
    }
    return `${url.pathname}${url.search}${url.hash}`
}

